import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SlidersHorizontal, Plus } from "lucide-react";
import { fmtDays } from "@/pages/hr/leave/leaveUtils";

export default function LeaveBalancesTab({ employees = [], types = [] }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ employee_id: "", leave_type_id: "" });
  const [adj, setAdj] = useState(null);

  const load = () => {
    const q = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/hr/leave/balances${q ? "?" + q : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  return (
    <div data-testid="leave-balances-tab">
      <div className="flex flex-wrap gap-2 mb-3 items-end justify-between rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex gap-2 items-end">
          <div><Label className="text-xs">Employee</Label>
            <Select value={f.employee_id || "all"} onValueChange={(v) => setF({ ...f, employee_id: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 w-48" data-testid="balances-filter-employee"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={f.leave_type_id || "all"} onValueChange={(v) => setF({ ...f, leave_type_id: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 w-36" data-testid="balances-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {can("leave:balance_manage") && <Button size="sm" className="bg-slate-900" data-testid="balances-adjust-btn" onClick={() => setAdj({ employee_id: "", leave_type_id: "", amount: "", reason: "", effective_date: "" })}><SlidersHorizontal className="w-4 h-4 mr-1.5" />Manual Adjustment</Button>}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="leave-balances-table">
          <thead><tr><th>Employee</th><th>Status</th><th>Leave Type</th><th>Balance</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={4} className="text-center text-slate-400 py-6">No balances</td></tr>
              : rows.map((r, i) => (
                <tr key={`${r.employee_id}-${r.leave_type_id}-${i}`} data-testid={`balance-row-${r.employee_id}-${r.leave_type_code}`}>
                  <td className="text-xs"><span className="font-mono">{r.employee_id}</span> {r.full_name}</td>
                  <td className="text-xs">{r.employment_status}</td>
                  <td className="text-xs">{r.leave_type_code} · {r.leave_type_name}</td>
                  <td className={`text-center font-semibold ${r.balance < 0 ? "text-rose-600" : ""}`}>{fmtDays(r.balance)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {adj && <AdjustDialog adj={adj} setAdj={setAdj} employees={employees} types={types} onDone={load} />}
    </div>
  );
}

function AdjustDialog({ adj, setAdj, employees, types, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!adj.employee_id || !adj.leave_type_id) return toast.error("Select employee and leave type");
    if (!adj.amount || Number(adj.amount) === 0) return toast.error("Amount cannot be zero");
    if (!adj.reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      await api.post("/hr/leave/adjustments", { employee_id: adj.employee_id, leave_type_id: adj.leave_type_id, amount: Number(adj.amount), reason: adj.reason, effective_date: adj.effective_date || null });
      toast.success("Adjustment recorded"); setAdj(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && setAdj(null)}>
      <DialogContent className="max-w-md" data-testid="leave-adjust-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" />Manual Balance Adjustment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Employee</Label>
            <Select value={adj.employee_id} onValueChange={(v) => setAdj({ ...adj, employee_id: v })}>
              <SelectTrigger data-testid="adjust-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Leave Type</Label>
            <Select value={adj.leave_type_id} onValueChange={(v) => setAdj({ ...adj, leave_type_id: v })}>
              <SelectTrigger data-testid="adjust-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} · {t.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Adjustment (+/- days)</Label><Input type="number" step="0.5" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid="adjust-amount" placeholder="e.g. 1 or -0.5" /></div>
          <div><Label className="text-xs">Effective Date</Label><Input type="date" value={adj.effective_date} onChange={(e) => setAdj({ ...adj, effective_date: e.target.value })} data-testid="adjust-date" /></div>
          <div><Label className="text-xs">Reason</Label><Textarea rows={2} value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="adjust-reason" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setAdj(null)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="adjust-submit" className="bg-slate-900">Save Adjustment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
